import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

/* The Curve's one chart. Renders real trailing history, and — when
   interactive — captures a thumb-drawn future snapped to weekly points.
   Also renders the crowd's band, your locked line, and reality's marks.

   Coordinate model: the trailing history occupies the left ~46% of the
   plot; the future (horizonWeeks weekly slots) fills the rest. All maths
   in viewBox units; values in, values out.

   `tall` = phone mode: a taller viewBox (more drawing room per CSS pixel)
   and larger type, since the whole SVG scales down to ~360px wide. */

const VB_W = 700;
const PAD_L = 52;
const PAD_R = 16;
const PAD_T = 34;
const PAD_B = 38;
const SPLIT = 0.46; // fraction of plot width given to history

export default function CurveChart({
  history = [],
  startValue,
  horizonWeeks = 8,
  band = null,
  marks = [],
  startDate = null,
  strands = null,
  myPoints = null,
  interactive = false,
  onDrawChange = null,
  todayLabel = 'TODAY', // floats above the anchor divider — pass '' to suppress
  // (e.g. Rewind uses startLabel instead, bottom-anchored with the other x-axis dates)
  startLabel = '', // bottom-left date, anchored on the same baseline as endLabel/ticks
  endLabel = '',
  endWeekLabel = '',
  ticks = [],
  tall = false,
  historySplit = SPLIT, // fraction of plot width given to history — Rewind (no trailing
  // history to show) passes a small value so the draw region fills the chart.
  drawHint = ['touch the dot,', 'draw his future →'],
  valueBounds = null, // {min, max} the domain must be able to reach even before
  // marks/myPoints exist to prove it — e.g. Rewind's multi-year windows can move
  // far outside the +/-% guess below (a suspension, a breakout).
}) {
  const svgRef = useRef(null);
  const [stroke, setStroke] = useState([]); // raw {x,y} while drawing
  const [snapped, setSnapped] = useState(null); // weekly values after release
  const drawingRef = useRef(false);
  // Mirror of `stroke` so pointer handlers never compute inside setState
  // updaters — React may replay updaters, and nested dispatches in one loop
  // forever ("maximum update depth exceeded").
  const strokeRef = useRef([]);

  const vbH = tall ? 560 : 340;
  const fs = tall ? 1.55 : 1; // type scale — the svg shrinks hard on phones

  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = vbH - PAD_T - PAD_B;
  const todayX = PAD_L + plotW * historySplit;
  const stepX = (plotW * (1 - historySplit)) / horizonWeeks;

  // y domain: generous head/leg room so drawings aren't cramped.
  const domain = useMemo(() => {
    const vals = [
      ...history.map((h) => h.value),
      startValue,
      ...(band ? [...band.p25, ...band.p75] : []),
      ...(marks || []).map((m) => m.value).filter((v) => v != null),
      ...(myPoints || []),
      ...(valueBounds ? [valueBounds.min, valueBounds.max] : []),
    ].filter((v) => v != null && Number.isFinite(v));
    // Draw room reflects realistic dynasty outcomes: even an ACL is about
    // a -25/30% event for an established asset (the dip gets bought), and a
    // true breakout runs about +40%. History/band/marks/valueBounds stretch
    // it further — valueBounds (Rewind) guarantees the real answer is always
    // reachable even before it's revealed, since the +/-% guess alone can be
    // far too tight over a multi-year window.
    const lo = Math.min(...vals, startValue * 0.7);
    const hi = Math.max(...vals, startValue * 1.4);
    const pad = (hi - lo) * 0.06;
    return { min: Math.max(0, lo - pad), max: hi + pad };
  }, [history, startValue, band, marks, myPoints, valueBounds]);

  const yOf = useCallback(
    (v) => PAD_T + plotH * (1 - (v - domain.min) / (domain.max - domain.min)),
    [domain, plotH]
  );
  const valOf = useCallback(
    (y) => domain.min + (domain.max - domain.min) * (1 - (y - PAD_T) / plotH),
    [domain, plotH]
  );
  const xOfWeek = (week) => todayX + stepX * week; // week 1..horizon

  // history xs spread across the left region
  const histPts = useMemo(() => {
    if (!history.length) return [];
    const span = todayX - PAD_L;
    return history.map((h, i) => ({
      x: PAD_L + (history.length === 1 ? span : (span * i) / (history.length - 1)),
      y: yOf(h.value),
    }));
  }, [history, todayX, yOf]);

  const anchorY = yOf(startValue);

  // ── drawing ──
  const clampY = useCallback((y) => Math.max(PAD_T, Math.min(PAD_T + plotH, y)), [plotH]);

  // Native listeners live in a ref-fed effect: React's root touch listeners
  // are passive, so synthetic preventDefault can't stop iOS Safari from
  // scrolling the page mid-draw. We need real non-passive listeners, pointer
  // capture, and a scroll lock for the duration of the gesture.
  const geomRef = useRef(null);
  geomRef.current = { todayX, plotW, horizonWeeks, valOf, clampY, xOfWeek, onDrawChange, vbH, anchorY, historySplit };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !interactive) return undefined;

    const lockScroll = () => {
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    };
    const unlockScroll = () => {
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };

    const point = (evt) => {
      const rect = svg.getBoundingClientRect();
      return {
        x: (evt.clientX - rect.left) * (VB_W / rect.width),
        y: (evt.clientY - rect.top) * (geomRef.current.vbH / rect.height),
      };
    };

    const onPointerDown = (evt) => {
      const g = geomRef.current;
      const p = point(evt);
      if (p.x < g.todayX - 30) return;
      drawingRef.current = true;
      lockScroll();
      try { svg.setPointerCapture(evt.pointerId); } catch (e) { /* older Safari */ }
      strokeRef.current = [];
      setSnapped(null);
      setStroke([]);
      if (g.onDrawChange) g.onDrawChange(null);
      evt.preventDefault();
    };

    const onPointerMove = (evt) => {
      if (!drawingRef.current) return;
      const g = geomRef.current;
      const p = point(evt);
      const x = Math.max(g.todayX + 1, Math.min(PAD_L + g.plotW, p.x));
      const prev = strokeRef.current;
      const y = g.clampY(p.y);
      let next;
      if (prev.length && x <= prev[prev.length - 1].x) {
        next = prev.slice();
        next[next.length - 1] = { x: next[next.length - 1].x, y };
      } else {
        next = [...prev, { x, y }];
      }
      strokeRef.current = next;
      setStroke(next);
      evt.preventDefault();
    };

    const onPointerUp = () => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      unlockScroll();
      const g = geomRef.current;
      const prev = strokeRef.current;
      if (!prev.length) return;
      strokeRef.current = [];
      setStroke([]);
      const reach = (prev[prev.length - 1].x - g.todayX) / (g.plotW * (1 - g.historySplit));
      if (reach < 0.75) {
        if (g.onDrawChange) g.onDrawChange(null);
        return; // not far enough — treat as a redraw attempt
      }
      const samples = [{ x: g.todayX, y: g.anchorY }, ...prev];
      const interpAt = (tx) => {
        if (tx <= samples[0].x) return samples[0].y;
        for (let j = 1; j < samples.length; j++) {
          if (samples[j].x >= tx) {
            const a = samples[j - 1];
            const b = samples[j];
            const t = (tx - a.x) / Math.max(b.x - a.x, 1e-6);
            return a.y + (b.y - a.y) * t;
          }
        }
        return samples[samples.length - 1].y;
      };
      const values = [];
      for (let week = 1; week <= g.horizonWeeks; week++) {
        values.push(Math.round(g.valOf(g.clampY(interpAt(g.xOfWeek(week))))));
      }
      setSnapped(values);
      if (g.onDrawChange) g.onDrawChange(values);
    };

    // The decisive iOS fix: a REAL non-passive touchmove that cancels the
    // scroll gesture while a draw is in progress.
    const onTouchGuard = (evt) => {
      if (drawingRef.current) evt.preventDefault();
    };

    svg.addEventListener('pointerdown', onPointerDown);
    svg.addEventListener('pointermove', onPointerMove);
    svg.addEventListener('pointerup', onPointerUp);
    svg.addEventListener('pointercancel', onPointerUp);
    svg.addEventListener('touchstart', onTouchGuard, { passive: false });
    svg.addEventListener('touchmove', onTouchGuard, { passive: false });
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      unlockScroll();
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', onPointerUp);
      svg.removeEventListener('pointercancel', onPointerUp);
      svg.removeEventListener('touchstart', onTouchGuard);
      svg.removeEventListener('touchmove', onTouchGuard);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [interactive]); // geometry rides geomRef so listeners bind once

  // ── render helpers ──
  const polyline = (pts) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const futureLine = (values) =>
    polyline([
      { x: todayX, y: anchorY },
      ...values.map((v, i) => ({ x: xOfWeek(i + 1), y: yOf(v) })),
    ]);

  const shown = myPoints || snapped;
  const gridVals = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) =>
      Math.round(domain.min + ((domain.max - domain.min) * i) / steps)
    );
  }, [domain]);

  const bandPath = useMemo(() => {
    if (!band || !band.p25 || !band.p25.length) return null;
    const upper = band.p75.map((v, i) => ({ x: xOfWeek(i + 1), y: yOf(v) }));
    const lower = band.p25.map((v, i) => ({ x: xOfWeek(i + 1), y: yOf(v) }));
    const up = [{ x: todayX, y: anchorY }, ...upper];
    const down = [...lower].reverse();
    return `M ${up.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')} L ${down
      .map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(' L ')} Z`;
  }, [band, anchorY, todayX, yOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marks are daily {date, value}; place them at fractional weeks when we
  // know the start date, else fall back to index-as-week (legacy shape).
  const markPts = (marks || [])
    .map((m, i) => {
      if (m.value == null) return null;
      let week = i + 1;
      if (startDate && m.date) {
        week = Math.min(
          horizonWeeks,
          (new Date(`${m.date}T00:00:00Z`) - new Date(`${startDate}T00:00:00Z`)) / (7 * 86400000)
        );
      }
      return { x: xOfWeek(week), y: yOf(m.value) };
    })
    .filter(Boolean);

  const fmtK = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v));
  const mono = "'SF Mono', ui-monospace, monospace";

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${vbH}`}
      style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', cursor: interactive ? 'crosshair' : 'default' }}
      role={interactive ? 'application' : 'img'}
      aria-label={interactive ? 'Draw the value line for the coming weeks' : 'Value chart'}
    >
      {/* grid */}
      {gridVals.map((v) => (
        <g key={v}>
          <line x1={PAD_L} y1={yOf(v)} x2={VB_W - PAD_R} y2={yOf(v)} stroke="#F0F0F2" strokeWidth="1" />
          <text x={PAD_L - 7} y={yOf(v) + 3.5 * fs} textAnchor="end" fontSize={9 * fs} fontWeight="600" fill="#86868B" fontFamily={mono}>
            {fmtK(v)}
          </text>
        </g>
      ))}

      {/* today divider */}
      <line x1={todayX} y1={PAD_T - 8} x2={todayX} y2={PAD_T + plotH + 6} stroke="#D2D2D7" strokeWidth="1.5" strokeDasharray="4 5" />
      {todayLabel ? (
        <text x={todayX} y={PAD_T - 15} textAnchor="middle" fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#6E6E73" fontFamily={mono}>
          {todayLabel}
        </text>
      ) : null}
      {startLabel ? (
        <text x={PAD_L} y={vbH - 12} textAnchor="start" fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#86868B" fontFamily={mono}>
          {startLabel}
        </text>
      ) : null}
      {endLabel && tall && endWeekLabel ? (
        <>
          <text x={VB_W - PAD_R} y={vbH - 12 - 11 * fs} textAnchor="end" fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#6E6E73" fontFamily={mono}>
            {endWeekLabel}
          </text>
          <text x={VB_W - PAD_R} y={vbH - 12} textAnchor="end" fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#6E6E73" fontFamily={mono}>
            {endLabel}
          </text>
        </>
      ) : endLabel ? (
        <text x={VB_W - PAD_R} y={vbH - 12} textAnchor="end" fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#6E6E73" fontFamily={mono}>
          {endWeekLabel ? `${endWeekLabel} · ${endLabel}` : endLabel}
        </text>
      ) : null}
      {ticks.map((t, i) => {
        const x = t.kind === 'week' ? xOfWeek(t.week) : (histPts[t.index] ? histPts[t.index].x : null);
        if (x == null) return null;
        return (
          <text key={`tk${i}`} x={x} y={vbH - 12} textAnchor={t.index === 0 ? 'start' : 'middle'} fontSize={9 * fs} fontWeight="700" letterSpacing="2" fill="#86868B" fontFamily={mono}>
            {t.label}
          </text>
        );
      })}

      {/* the crowd's band */}
      {bandPath ? <path d={bandPath} fill="#EFE4D2" opacity="0.75" /> : null}

      {/* the crowd itself — everyone else's lines as faint strands */}
      {(strands || []).map((pts, i) => (
        <polyline
          key={`st${i}`}
          points={futureLine(pts)}
          fill="none"
          stroke="#C0B49B"
          strokeOpacity="0.5"
          strokeWidth={tall ? 2.5 : 1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {/* history */}
      {histPts.length > 1 ? (
        <polyline points={polyline(histPts)} fill="none" stroke="#1D1D1F" strokeWidth={tall ? 4 : 3} strokeLinecap="round" strokeLinejoin="round" />
      ) : null}
      <circle cx={todayX} cy={anchorY} r={tall ? 8 : 5} fill="#1D1D1F" />
      {interactive && !shown && !stroke.length ? (
        <circle cx={todayX} cy={anchorY} r={tall ? 16 : 11} fill="#FF7A00" opacity="0.25">
          <animate attributeName="r" values={`${tall ? 12 : 8};${tall ? 20 : 14};${tall ? 12 : 8}`} dur="1.6s" repeatCount="indefinite" />
        </circle>
      ) : null}

      {/* reality's marks */}
      {markPts.length ? (
        <>
          <polyline points={polyline([{ x: todayX, y: anchorY }, ...markPts])} fill="none" stroke="#4E7FD0" strokeWidth={tall ? 4 : 3} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={markPts[markPts.length - 1].x} cy={markPts[markPts.length - 1].y} r={tall ? 6.5 : 4.5} fill="#4E7FD0" />
        </>
      ) : null}

      {/* my line (locked or snapped) */}
      {shown && shown.length ? (
        <polyline points={futureLine(shown)} fill="none" stroke="#FF7A00" strokeWidth={tall ? 5 : 3.5} strokeLinecap="round" strokeLinejoin="round" />
      ) : null}

      {/* live raw stroke while drawing */}
      {stroke.length ? (
        <polyline points={polyline([{ x: todayX, y: anchorY }, ...stroke])} fill="none" stroke="#FF7A00" strokeWidth={tall ? 5 : 3.5} strokeOpacity="0.8" strokeLinecap="round" strokeLinejoin="round" />
      ) : null}

      {/* hint */}
      {interactive && !shown && !stroke.length ? (
        <g fontFamily="inherit" fill="#A8A8AD" textAnchor="middle" fontSize={11.5 * fs}>
          <text x={todayX + (plotW * (1 - historySplit)) / 2} y={anchorY - 34 * fs}>{drawHint[0]}</text>
          <text x={todayX + (plotW * (1 - historySplit)) / 2} y={anchorY - 18 * fs}>{drawHint[1]}</text>
        </g>
      ) : null}
    </svg>
  );
}
